import { Adapter, WalletReadyState } from '@solana/wallet-adapter-base';
import {
	ConnectionProvider,
	WalletProvider,
} from '@solana/wallet-adapter-react';
import axios from 'axios';
import { ThemeProvider } from 'next-themes';
import dynamic from 'next/dynamic';
import { PropsWithChildren, useMemo, useState } from 'react';
import AuctionDataProvider from 'src/providers/auctionDataProvider';
import CurrentSlotProvider from 'src/providers/currentSlotProvider';
import DriftMarketEventSubscriberProvider from 'src/providers/driftMarketEventSubscriberProvider';
import OrderRecordMatcherProvider from 'src/providers/orderRecordMatcherProvider';
import PHProvider from 'src/providers/postHogProvider';
import { SWRConfig } from 'swr';
import { DRIFT_WALLET_PROVIDERS } from '../constants/wallets';
import Env, { DriftTheme } from '../environmentVariables/EnvironmentVariables';
import useInterval from '../hooks/useInterval';
import CandlesProvider from '../providers/candles/candleClientProvider';
import OrderSubscriberProvider from '../providers/orderSubscriberProvider';
import OrderbookDisplayDataProvider from '../providers/orderbookDisplayProvider';
import SettingsProvider from '../providers/settingsProvider';
import DevTopWrapper from './Dev/DevTopWrapper';
import ErrorBoundary from './ErrorBoundary/ErrorBoundary';
import FloatingUI from './FloatingUI';
import SweepStakesEventSubscriberProvider from 'src/providers/sweepstakesEventSubscriberProvider';
import WebsocketSubscriptionProvider from '../providers/websockets/websocketSubscriptionProvider';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import IfStakeSubscriberProvider from 'src/providers/ifStakeSubscriberProvider';
import MetricsProvider from '../providers/metrics/MetricsProvider';
import utc from 'dayjs/plugin/utc';
import SafePushProvider from '../providers/safePushProvider';

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);

const AsyncAppSetup = dynamic(() => import('./AsyncAppSetup'));
/**
 * Note: previously this component was in charge of running async app setup hooks once the window.load event fired. After updating to React 18 the window.load event stopped firing - so that functionality has temporarily been disabled. That is why we still have a "liteStore" and "onLoad" event hook which aren't doing anything. Once SSR has been properly set up on the app which is the goal of updating to react 18 anyway, then the problem this component was trying to solve will be finished.
 * @param props
 * @returns
 */
const AppWrapper = (props: PropsWithChildren<any>) => {
	const [e2eWallet, setE2eWallet] = useState<Adapter>(undefined);

	useInterval(() => {
		const E2E_WALLET = Env.E2E_WALLET;

		//@ts-ignore
		if (window.useE2eWallet === true) {
			setE2eWallet(Env.E2E_WALLET);
		} else {
			if (E2E_WALLET.connected) {
				E2E_WALLET.disconnect();
			}
			setE2eWallet(undefined);
		}
	}, 1000);

	const wallets = useMemo(
		() =>
			e2eWallet
				? [...DRIFT_WALLET_PROVIDERS, e2eWallet]
				: DRIFT_WALLET_PROVIDERS,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[e2eWallet]
	);

	const sortedWallets = useMemo(
		() =>
			[...wallets].sort((a, b) => {
				const aIsInstalled =
					a.readyState === WalletReadyState.Loadable ||
					a.readyState === WalletReadyState.Installed;

				const bIsInstalled =
					b.readyState === WalletReadyState.Loadable ||
					b.readyState === WalletReadyState.Installed;

				if (aIsInstalled && !bIsInstalled) return -1;
				if (aIsInstalled === bIsInstalled) return 0;
				return 1;
			}),
		[wallets]
	);

	return (
		<>
			<ErrorBoundary>
				<ThemeProvider
					attribute="class"
					defaultTheme={DriftTheme.dark}
					themes={[DriftTheme.dark, DriftTheme.light]}
					enableSystem={false}
				>
					<SafePushProvider>
						<PHProvider>
							<SWRConfig
								value={{
									fetcher: (url: string) =>
										axios.get(url).then((res) => res.data),
								}}
							>
								<ConnectionProvider endpoint={Env.defaultRpcAddress}>
									<WebsocketSubscriptionProvider>
										<WalletProvider
											wallets={sortedWallets}
											// We run our own custom autoconnect logic in the useKeepWalletsInSync hook
											autoConnect={false}
											localStorageKey="walletName"
										>
											<SettingsProvider>
												<IfStakeSubscriberProvider>
													<OrderSubscriberProvider>
														<DriftMarketEventSubscriberProvider>
															<SweepStakesEventSubscriberProvider>
																<CurrentSlotProvider>
																	<OrderbookDisplayDataProvider>
																		<CandlesProvider>
																			<OrderRecordMatcherProvider>
																				<AuctionDataProvider>
																					<MetricsProvider>
																						<>
																							<DevTopWrapper />
																							{props.children}
																							<FloatingUI />
																						</>
																						<AsyncAppSetup />
																					</MetricsProvider>
																				</AuctionDataProvider>
																			</OrderRecordMatcherProvider>
																		</CandlesProvider>
																	</OrderbookDisplayDataProvider>
																</CurrentSlotProvider>
															</SweepStakesEventSubscriberProvider>
														</DriftMarketEventSubscriberProvider>
													</OrderSubscriberProvider>
												</IfStakeSubscriberProvider>
											</SettingsProvider>
										</WalletProvider>
									</WebsocketSubscriptionProvider>
								</ConnectionProvider>
							</SWRConfig>
						</PHProvider>
					</SafePushProvider>
				</ThemeProvider>
			</ErrorBoundary>
		</>
	);
};

export default AppWrapper;
